import React, { useState, useEffect, useRef } from 'react';
import { X, Search } from 'lucide-react';
import { Product, Menu } from '../services/orderLinkAPI';
import { getDirectImageUrl } from '../utils/imageUrl';

interface ProductWithCategory {
  product: Product;
  category: string;
}

interface SearchModalProps {
  isOpen: boolean;
  onClose: () => void;
  menu: Menu;
  onProductSelect: (productId: string, category: string) => void;
}

const SearchModal: React.FC<SearchModalProps> = ({
  isOpen,
  onClose,
  menu,
  onProductSelect,
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredProducts, setFilteredProducts] = useState<ProductWithCategory[]>([]);
  const inputRef = useRef<HTMLInputElement>(null);

  // Focus input when modal opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  // Clear search when modal closes
  useEffect(() => {
    if (!isOpen) {
      setSearchTerm('');
      setFilteredProducts([]);
    }
  }, [isOpen]);

  // Filter products based on search term
  useEffect(() => {
    if (!searchTerm.trim()) {
      setFilteredProducts([]);
      return;
    }

    const term = searchTerm.toLowerCase();
    const results: ProductWithCategory[] = [];

    (Object.entries(menu) as [string, Product[]][]).forEach(([category, products]) => {
      products.forEach((product) => {
        const productNameMatch = product.name.toLowerCase().includes(term);
        const categoryMatch = category.toLowerCase().includes(term);

        if (productNameMatch || categoryMatch) {
          results.push({ product, category });
        }
      });
    });

    // Limit to 10 results
    setFilteredProducts(results.slice(0, 10));
  }, [searchTerm, menu]);

  const handleProductClick = (productId: string, category: string) => {
    onProductSelect(productId, category);
    onClose();
  };

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-start justify-center pt-4 px-4"
      onClick={handleOverlayClick}
    >
      <div className="w-full max-w-2xl bg-white rounded-lg shadow-xl overflow-hidden">
        {/* Header */}
        <div className="flex items-center gap-3 p-4 border-b border-gray-200">
          <Search className="w-5 h-5 text-gray-400 flex-shrink-0" />
          <input
            ref={inputRef}
            type="text"
            value={searchTerm}
            onChange={(e: any) => setSearchTerm(e.target.value)}
            placeholder="Buscar produtos..."
            className="flex-1 text-[16px] outline-none placeholder-gray-400"
          />
          <button
            onClick={onClose}
            className="p-1 hover:bg-gray-100 rounded-full transition-colors"
          >
            <X className="w-5 h-5 text-gray-600" />
          </button>
        </div>

        {/* Results Dropdown */}
        {searchTerm.trim() && (
          <div className="max-h-[60vh] overflow-y-auto">
            {filteredProducts.length > 0 ? (
              <div className="py-2">
                {filteredProducts.map(({ product, category }) => (
                  <button
                    key={`${product.id}-${category}`}
                    onClick={() => handleProductClick(product.id, category)}
                    className="w-full px-4 py-3 flex items-center gap-3 hover:bg-gray-100 transition-colors text-left"
                  >
                    {/* Product Image */}
                    <div className="w-12 h-12 bg-gray-100 rounded-lg overflow-hidden flex-shrink-0">
                      <img
                        src={getDirectImageUrl(product.image_path)}
                        alt={product.name}
                        className="w-full h-full object-cover"
                        onError={(e: any) => {
                          e.currentTarget.src = '/no_icon.png';
                        }}
                      />
                    </div>

                    {/* Product Info */}
                    <div className="flex-1 min-w-0">
                      <p className="text-[15px] font-semibold text-gray-900 truncate">
                        {product.name}
                      </p>
                      <p className="text-[13px] text-gray-500 truncate">
                        {category}
                      </p>
                    </div>

                    {/* Price */}
                    <div className="text-right flex-shrink-0">
                      <p className="text-[15px] font-bold" style={{ color: 'var(--text-color)' }}>
                        R$ {product.price.toFixed(2).replace('.', ',')}
                      </p>
                      {product.eh_promocao && (
                        <span
                          className="inline-block px-1.5 py-0.5 text-[10px] font-bold text-white rounded mt-1"
                          style={{ backgroundColor: 'var(--badge-color)' }}
                        >
                          PROMO
                        </span>
                      )}
                    </div>
                  </button>
                ))}
              </div>
            ) : (
              <div className="py-12 text-center text-gray-500">
                <Search className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                <p className="text-[15px]">Nenhum produto encontrado</p>
                <p className="text-[13px] text-gray-400 mt-1">
                  Tente buscar por outro termo
                </p>
              </div>
            )}
          </div>
        )}

        {/* Empty State */}
        {!searchTerm.trim() && (
          <div className="py-12 text-center text-gray-500">
            <Search className="w-12 h-12 mx-auto mb-3 text-gray-300" />
            <p className="text-[15px]">Digite para buscar produtos</p>
            <p className="text-[13px] text-gray-400 mt-1">
              Pesquise por nome ou categoria
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default SearchModal;
